/**
 * Map an HTTP method + URL path to { resource_type, action }.
 * Mirrors the backend _RESOURCE_PATTERNS and _ACTION_OVERRIDES tables
 * so classification is consistent on both sides.
 */

// Ordered: more-specific patterns first so they win over shorter prefixes.
const RESOURCE_PATTERNS = [
  [/\/workflows\/periods/, 'period'],
  [/\/workflows/, 'workflow'],
  [/\/workflow-groups/, 'workflow'],
  [/\/periods\/[^/]+\/files/, 'file'],
  [/\/periods/, 'period'],
  [/\/input-funds/, 'fund'],
  [/\/fund-mappings/, 'fund'],
  [/\/funds/, 'fund'],
  [/\/files/, 'file'],
  [/extra-prompts/, 'prompt_message'],
  [/national-code/, 'national_code'],
  [/jurisdiction/, 'jurisdiction'],
  [/generate-pdf-report/, 'report'],
  [/\/conversion\/parse/, 'report'],
  [/\/report/, 'report'],
  [/\/auto-extract/, 'extraction'],
  [/\/extract/, 'extraction'],
  [/\/edit-note/, 'transformation'],
  [/\/create-duplicate-extraction/, 'transformation'],
  [/\/retry-smart-transformation/, 'transformation'],
  [/\/transform/, 'transformation'],
  [/automate-report/, 'report'],
  [/\/automat/, 'automation'],
  [/\/fca-users/, 'user'],
  [/\/user-groups/, 'user'],
  [/\/users/, 'user'],
  [/\/threads/, 'comment'],
  [/\/invite/, 'approver'],
  [/\/approvers/, 'approver'],
  [/\/companies/, 'company'],
  [/\/company/, 'company'],
  [/\/super-admin/, 'user']
];

const ACTION_OVERRIDES = [
  [/\/approve/, 'APPROVE'],
  [/\/submit/, 'SUBMIT'],
  [/\/resolve/, 'RESOLVE'],
  [/\/export/, 'EXPORT'],
  [/generate-pdf-report/, 'EXPORT'],
  [/create_xml_zip/, 'EXPORT'],
  [/automate-report/, 'RUN'],
  [/\/conversion\/parse/, 'REGENERATE'],
  [/force-update/, 'REGENERATE'],
  [/\/auto-extract/, 'RUN'],
  [/\/retry-smart-transformation/, 'RUN'],
  [/\/edit-note/, 'UPDATE'],
  [/\/set-password/, 'UPDATE'],
  [/\/cancel/, 'CANCEL'],
  [/\/duplicate/, 'CREATE'],
  [/\/fund-mappings\/save/, 'UPDATE'],
  [/\/invite/, 'INVITE'],
  [/\/deactivate/, 'DEACTIVATE']
];

const METHOD_ACTION = { POST: 'CREATE', PUT: 'UPDATE', PATCH: 'UPDATE', DELETE: 'DELETE' };

/**
 * @param {string} method  HTTP method (GET, POST, PUT, PATCH, DELETE)
 * @param {string} path    URL path (e.g. "api/input-funds/abc-123")
 * @returns {{ resource_type: string|null, action: string|null }}
 */
export function classify(method, path) {
  const m = (method || '').toUpperCase();

  let resource_type = null;
  for (const [re, type] of RESOURCE_PATTERNS) {
    if (re.test(path)) {
      resource_type = type;
      break;
    }
  }

  let action = null;
  for (const [re, act] of ACTION_OVERRIDES) {
    if (re.test(path)) {
      // INVITE override is only for POST (sending invitation); DELETE keeps DELETE
      if (act === 'INVITE' && m !== 'POST') continue;
      action = act;
      break;
    }
  }
  if (!action) action = METHOD_ACTION[m] ?? null;

  // Comment endpoints always use the COMMENT action
  if (resource_type === 'comment') action = 'COMMENT';

  return { resource_type, action };
}
